import {
  SkyButton,
  SkyDialog,
  SkyLink,
  SkyMessageBar,
  SkyTabList,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useMemo, useRef, useState, type ComponentProps, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "wouter";
import {
  EmptyState,
  ErrorState,
  LoadingRows,
  PermissionNotice,
} from "@/components/access/ScreenStates";
import { DestinationCredentials } from "@/components/credentials/DestinationCredentials";
import { DestinationTestPanel } from "@/components/destination/DestinationTestPanel";
import { TestReauthDialog } from "@/components/destination/TestReauthDialog";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import {
  DbtSourceForm,
  buildDbtBody,
  dbtDraftFromBinding,
  type DbtDraft,
  type DbtSourceSaveBody,
} from "@/components/settings/DbtSourceForm";
import {
  DestinationForm,
  FIELD_LABELS,
  WAREHOUSE_TEXT_FIELDS,
  destinationRefusals,
  draftFromBinding,
  metadataColumnsChanged,
  type DestinationDraft,
  type DestinationRefusals,
} from "@/components/settings/DestinationForm";
import { PlatformResources } from "@/components/settings/PlatformResources";
import { ReauthDialog } from "@/components/settings/ReauthDialog";
import { ReplaceSecretDialog } from "@/components/settings/ReplaceSecretDialog";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import { InlineErrorBar, useInlineError, type InlineFailure } from "@/lib/feedback/InlineErrorBar";
import { useDismissNoticeWhen, useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import {
  DBT_SOURCE_PATH,
  secretFieldIssues,
  useBinding,
  useSaveBinding,
  type BindingSaveState,
} from "@/hooks/use-binding";
import { useDestinationTestRun } from "@/hooks/use-destination-test-run";
import { usePermissions } from "@/hooks/use-permissions";
import { useInstance } from "@/hooks/use-settings";
import { ApiClientError, isApiErrorCode } from "@/lib/api";
import { isNotFoundError } from "@/components/access/RecordNotFound";
import { ROUTES, absolutePath } from "@/lib/routes";
import { TABLE_NAME_CASE_LABELS, type InstanceBindingView } from "@shared/types/instances";
import type { DestinationTestView } from "@shared/types/instancesAdmin";
import {
  isSnowflakeOnlyTableNameCase,
  type TableNameCase,
} from "@shared/warehouse/targetTableName";
import { SNOWFLAKE_LOAD_FIELDS } from "@shared/warehouse/snowflakeLoadFields";
import {
  isBindingSecretCopy,
  suggestedWarehouseDatabase,
  type BindingSecretField,
  type PlatformUpdateBindingBody,
  type UpdateBindingBody,
} from "@shared/types/instancesAdmin";

/** The Warehouse save: value-only on the instance route, or with a copy on the platform route. */
type WarehouseSaveBody = UpdateBindingBody | PlatformUpdateBindingBody;

/**
 * The destination (canvas 08; FR-21, FR-14, SEC-2, SEC-4), in two page tabs since
 * Session 10 (P5): **Warehouse** (`instance.binding.write`, PlatformAdmin) and
 * **dbt repository** (`dbt_jobs.write`, InstanceAdmin and above),
 * switched by `?tab=` so a link lands on the right one. Everyone who can read the
 * binding sees both, read-only where they cannot write. Each tab has its own draft
 * and its own save, both held here so a tab switch or a re-authentication redirect
 * loses nothing, and each save replays only its own held body.
 *
 * The draft lives HERE, above the save, because FR-14 can interrupt a save with a
 * request for a fresher identity and the form has to survive it untouched. A
 * draft owned by the form would be remounted by the dialog and the user would
 * lose every field they had changed — which is the one behaviour the acceptance
 * test for this screen asserts.
 *
 * The body sent on save is a DIFF against the loaded binding, so a field nobody
 * touched is absent rather than re-sent as its own value, and a secret the screen
 * only ever saw as a pointer is never round-tripped.
 */

const SECRET_LABELS: Record<
  BindingSecretField,
  { title: string; label: string; multiline: boolean }
> = {
  snowflakePrivateKey: {
    title: "Replace the Snowflake private key",
    label: "Private key (PEM)",
    multiline: true,
  },
  snowflakeKeyPassphrase: {
    title: "Replace the key passphrase",
    label: "Key passphrase (only for an encrypted key)",
    multiline: false,
  },
  databricksSpSecret: {
    title: "Replace the service principal secret",
    label: "Client secret",
    multiline: false,
  },
  dbtGitSecret: {
    title: "Replace the dbt repository credential",
    // A token only: ADR 0004 rules out SSH and deploy keys (the image clones
    // over https with the token as the password).
    label: "Personal access token",
    multiline: false,
  },
  teamsWebhook: {
    title: "Replace the Teams webhook",
    label: "Webhook URL",
    multiline: false,
  },
};

/** One changed credential: the fact and, for a copy, the vault secret's name; never the value. */
function secretChangeLine(
  field: string,
  value: Exclude<DestinationDraft["secrets"][keyof DestinationDraft["secrets"]], undefined>,
): string {
  if (value === null) return `${field}: cleared`;
  if (isBindingSecretCopy(value)) return `${field}: copy of ${value.copyFrom.name}`;
  return `${field}: new value`;
}

/** What changed, in the reader's terms, for the re-authentication dialog. */
function summariseChanges(draft: DestinationDraft, binding: InstanceBindingView): string[] {
  const changes: string[] = [];
  if (draft.destinationType !== binding.destinationType) {
    changes.push(`Destination type: ${binding.destinationType} → ${draft.destinationType}`);
  }
  // #2501: a field the deployment fixes is never named, as it is never shown.
  const caseFixed = binding.fixedByDeployment?.includes("tableNameCase") === true;
  const caseChange = caseFixed ? null : tableNameCaseChange(draft, binding);
  if (caseFixed) {
    // Nothing: the server keeps the fixed case on a move too.
  } else if (caseChange) {
    changes.push(
      `Table name case: ${TABLE_NAME_CASE_LABELS[binding.tableNameCase ?? "native"]} → ${TABLE_NAME_CASE_LABELS[caseChange]}`,
    );
  } else if (
    // Review r2: the body does not send it, but the server stores Native for a Keep source case
    // binding that moves to Databricks (`BindingService.write`), so the summary says so.
    draft.destinationType === "databricks" &&
    binding.destinationType !== "databricks" &&
    isSnowflakeOnlyTableNameCase(binding.tableNameCase)
  ) {
    const before = binding.tableNameCase === "lower" ? "lower" : "source";
    changes.push(
      `Table name case: ${TABLE_NAME_CASE_LABELS[before]} → ${TABLE_NAME_CASE_LABELS.native} (Databricks uses Native)`,
    );
  }
  if (metadataColumnsChanged(draft, binding)) {
    changes.push(
      `skyData columns: ${onOff(!draft.metadataColumns)} → ${onOff(draft.metadataColumns)}`,
    );
  }
  for (const key of WAREHOUSE_TEXT_FIELDS) {
    const before = binding[key] ?? "";
    if (draft.text[key] !== before) {
      changes.push(`${FIELD_LABELS[key]}: ${before || "empty"} → ${draft.text[key] || "empty"}`);
    }
  }
  for (const [field, value] of Object.entries(draft.secrets)) {
    // The VALUE never appears, only the fact that this credential changed (and, for a copy,
    // the name of the vault secret it is copied from: an identifier, not a value).
    changes.push(secretChangeLine(field, value));
  }
  return changes;
}

const onOff = (on: boolean): string => (on ? "on" : "off");

/**
 * The table name case to send, or null. On Databricks `source` and `lower` are never sent (the
 * server refuses them, and moves such a binding there back to `native` itself).
 */
function tableNameCaseChange(
  draft: DestinationDraft,
  binding: InstanceBindingView,
): TableNameCase | null {
  const before = binding.tableNameCase ?? "native";
  if (draft.tableNameCase === before) return null;
  if (draft.destinationType === "databricks" && isSnowflakeOnlyTableNameCase(draft.tableNameCase)) {
    return null;
  }
  return draft.tableNameCase;
}

function buildBody(
  draft: DestinationDraft,
  binding: InstanceBindingView,
): WarehouseSaveBody | null {
  const body: Record<string, unknown> = {};
  if (draft.destinationType !== binding.destinationType) {
    body.destinationType = draft.destinationType;
  }
  const caseChange = tableNameCaseChange(draft, binding);
  if (caseChange) body.tableNameCase = caseChange;
  if (metadataColumnsChanged(draft, binding)) body.metadataColumns = draft.metadataColumns;
  // Warehouse fields only: storage is the platform's and the dbt fields are the other tab's.
  for (const key of WAREHOUSE_TEXT_FIELDS) {
    const before = binding[key] ?? "";
    const after = draft.text[key];
    if (after === before) continue;
    // An emptied field clears the column; the schema takes null for exactly that.
    body[key] = after === "" ? null : after;
  }
  const secrets = Object.fromEntries(
    Object.entries(draft.secrets).filter(([field]) => field !== "dbtGitSecret"),
  );
  if (Object.keys(secrets).length > 0) body.secrets = secrets;
  return Object.keys(body).length > 0 ? (body as WarehouseSaveBody) : null;
}

type DestinationTab = "warehouse" | "dbt";

const TAB_PATHS: Record<DestinationTab, string> = {
  warehouse: ROUTES.settingsDestination,
  dbt: `${ROUTES.settingsDestination}?tab=dbt`,
};

/** The header Save's failure notice, replaced by the next failure and cleared by a success. */
const SAVE_ERROR_NOTICE_KEY = "destination-save-error";
const SAVED_NOTICE_KEY = "destination-saved";
const DBT_SAVED_NOTICE_KEY = "transformation-source-saved";
/** #924: a Save refused for empty load fields; the fields themselves say what to fill in. */
const INCOMPLETE_NOTICE =
  "The destination was not saved. Fill in the highlighted fields, then save again.";
/** #1248: an incomplete save that the server accepted, because the fields were filled elsewhere. */
const FILLED_ELSEWHERE_NOTICE =
  "Destination saved. Someone else filled in the remaining fields meanwhile, so check them before the next import.";

const LOAD_FIELDS: ReadonlySet<string> = new Set(SNOWFLAKE_LOAD_FIELDS);

/** The same five as the server's refusal details name them (the key under `secrets.`). */
const SERVER_LOAD_FIELDS: ReadonlySet<string> = new Set(
  SNOWFLAKE_LOAD_FIELDS.map((field) =>
    field === "snowflakePrivateKey" ? `secrets.${field}` : field,
  ),
);

/**
 * #1248: whether every refusal is the server's own rule (#924's load fields), which the binding
 * save refuses and records as a failed attempt in the audit trail. Such a save is still sent, so
 * the refusal is the server's and leaves its row. #925's passphrase check reads the key's PEM
 * header in the browser only, so a save it refuses is never sent, even with a load field empty
 * too: the server would not refuse that pair, and a concurrent fill of the empty field would
 * otherwise write a key and passphrase the form had refused.
 */
function serverRefuses(refusals: DestinationRefusals): boolean {
  const fields = Object.keys(refusals);
  return fields.length > 0 && fields.every((field) => LOAD_FIELDS.has(field));
}

/**
 * #1248: the server's #924 refusal itself, a 400 `VALIDATION_FAILED` naming only load fields.
 * Decided from the error, never from which attempt sent it, so a save replayed after a fresh
 * sign-in (the dialog's retry, or the body carried across a redirect) reads the same. Any other
 * failure (network, 5xx, 429, a 409, a lost session) keeps its own sentence and reference.
 */
function isLoadFieldRefusal(cause: unknown): boolean {
  return (
    cause instanceof ApiClientError &&
    cause.status === 400 &&
    cause.code === "VALIDATION_FAILED" &&
    cause.details.length > 0 &&
    cause.details.every(
      (detail) => detail.field !== undefined && SERVER_LOAD_FIELDS.has(detail.field),
    )
  );
}

/** The binding's read for a tab: loading, none bound yet, its failure, or the tab's own content. */
function BindingRead({
  isPending,
  notFound,
  isError,
  error,
  onRetry,
  emptyMessage,
  children,
}: {
  isPending: boolean;
  notFound: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  emptyMessage: string;
  children: ReactNode;
}) {
  if (isPending) return <LoadingRows rows={4} columns={3} />;
  if (notFound) {
    return <EmptyState title="This instance has no destination yet" message={emptyMessage} />;
  }
  if (isError) {
    return <ErrorState title="The destination did not load" error={error} onRetry={onRetry} />;
  }
  return children;
}

/** The dbt repository form, once the binding and its draft are both there. */
function DbtTabForm({
  binding,
  draft,
  ...rest
}: {
  binding: InstanceBindingView | undefined;
  draft: DbtDraft | null;
  canEdit: boolean;
  allowCopy: boolean;
  saver: BindingSaveState<DbtSourceSaveBody>;
  onDraftChange: (draft: DbtDraft) => void;
}) {
  if (!binding || !draft) return null;
  return <DbtSourceForm binding={binding} draft={draft} {...rest} />;
}

/** Round 9 (B-02): the save refused because tables have landed, offered again with a reset. */
function MoveRefusedDialog({
  moveRefused,
  isSaving,
  onKeep,
  onSaveAgain,
}: {
  moveRefused: MoveRefused | null;
  isSaving: boolean;
  onKeep: () => void;
  onSaveAgain: () => void;
}) {
  return (
    <SkyDialog
      open={moveRefused !== null}
      onOpenChange={(open) => {
        if (!open) onKeep();
      }}
      size="medium"
      testId="destination-move-dialog"
      title="Read everything again in the new destination?"
      description={moveRefused?.reason}
      primaryAction={{
        label: "Save and read everything again",
        destructive: true,
        loading: isSaving,
        onClick: onSaveAgain,
      }}
      secondaryAction={{
        label: "Keep the current destination",
        onClick: onKeep,
      }}
    />
  );
}

/** Who may change a destination, said above the form. */
function WarehouseIntro({ canWrite }: { canWrite: boolean }) {
  if (canWrite) {
    return (
      <SkyMessageBar
        intent="info"
        layout="multiline"
        title="Only a platform admin changes a destination"
        // #949: recent sign-in on a privileged action is off by default since 2026-10-06
        // (docs/rbac.md, "Recent authentication on a privileged action"), and this page does not
        // know whether a deployment turned it on, so it promises no extra step.
        message="The change applies to the next run."
      />
    );
  }
  return (
    <SkyMessageBar
      intent="warning"
      layout="multiline"
      testId="binding-read-only"
      title="You can read this destination but not change it"
      message="Only a platform admin can change where data lands. Ask one to make the change."
    />
  );
}

/**
 * The Warehouse form with its credentials, the writer's save controls, the secret dialog and the
 * re-authentication hold.
 */
function WarehouseEditor({
  binding,
  draft,
  onDraftChange,
  canWrite,
  secretErrors,
  refusals,
  suggestedDatabase,
  saver,
  saveable,
  saveFailure,
  onDismissSaveFailure,
  onSave,
  discardable,
  replacing,
  onReplacingChange,
  testPanel,
}: {
  binding: InstanceBindingView;
  draft: DestinationDraft;
  onDraftChange: (draft: DestinationDraft) => void;
  canWrite: boolean;
  secretErrors: ReturnType<typeof secretFieldIssues>;
  /** The fields a refused Save named (#924, #925), until each is fixed. */
  refusals: DestinationRefusals;
  suggestedDatabase: string | undefined;
  saver: BindingSaveState<WarehouseSaveBody>;
  saveable: boolean;
  saveFailure: InlineFailure | null;
  onDismissSaveFailure: () => void;
  /** Saves, noting which Save was pressed: its failure is said beside it. */
  onSave: (origin: "header" | "form") => void;
  /** The form differs from the binding. */
  discardable: boolean;
  replacing: BindingSecretField | null;
  onReplacingChange: (field: BindingSecretField | null) => void;
  /** #923: the state chip, the last test and Test, Save and test, Check now. */
  testPanel?: ReactNode;
}) {
  return (
    <>
      {testPanel}
      <DestinationForm
        binding={binding}
        draft={draft}
        readOnly={!canWrite}
        onTypeChange={(destinationType) => onDraftChange({ ...draft, destinationType })}
        onCaseChange={(tableNameCase) => onDraftChange({ ...draft, tableNameCase })}
        onMetadataColumnsChange={(metadataColumns) => onDraftChange({ ...draft, metadataColumns })}
        onTextChange={(field, value) =>
          onDraftChange({ ...draft, text: { ...draft.text, [field]: value } })
        }
        onReplaceSecret={onReplacingChange}
        onClearSecret={(field) =>
          onDraftChange({ ...draft, secrets: { ...draft.secrets, [field]: null } })
        }
        onDiscardSecret={(field) => {
          const secrets = { ...draft.secrets };
          delete secrets[field];
          onDraftChange({ ...draft, secrets });
        }}
        secretErrors={secretErrors}
        refusals={refusals}
        {...(suggestedDatabase ? { suggestedDatabase } : {})}
      />

      {/* 2026-09-28 cred-check: what the daily check found and each expiry date. */}
      <DestinationCredentials binding={binding} canEdit={canWrite} />

      {canWrite ? (
        <>
          <TopbarActionsPortal>
            <SkyButton
              appearance="primary"
              testId="save-destination-topbar"
              disabled={!saveable}
              loading={saver.isSaving}
              onClick={() => onSave("header")}
            >
              Save changes
            </SkyButton>
          </TopbarActionsPortal>

          <InlineErrorBar
            error={saveFailure}
            title={saveFailure?.retryable ? undefined : "We could not save this destination"}
            onDismiss={onDismissSaveFailure}
            testId="destination-save-error"
          />

          <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
            <SkyText as="p" size={200} color="muted">
              This change affects the next run only. A run already in flight keeps the destination
              it started with.
            </SkyText>
            <span className="ml-auto flex items-center gap-2">
              <SkyButton
                appearance="subtle"
                testId="discard-destination"
                disabled={!discardable}
                onClick={() => {
                  onDraftChange(draftFromBinding(binding));
                  // #1092 (sibling): the refusal was about the edit just thrown away.
                  onDismissSaveFailure();
                }}
              >
                Discard
              </SkyButton>
              <SkyButton
                appearance="primary"
                testId="save-destination"
                disabled={!saveable}
                loading={saver.isSaving}
                onClick={() => onSave("form")}
              >
                Save changes
              </SkyButton>
            </span>
          </div>
        </>
      ) : null}

      <ReplaceSecretDialog
        open={replacing !== null}
        title={replacing ? SECRET_LABELS[replacing].title : ""}
        label={replacing ? SECRET_LABELS[replacing].label : ""}
        multiline={replacing ? SECRET_LABELS[replacing].multiline : true}
        onOpenChange={(open) => {
          if (!open) onReplacingChange(null);
        }}
        onStage={(value) => {
          if (!replacing) return;
          onDraftChange({ ...draft, secrets: { ...draft.secrets, [replacing]: value } });
        }}
        // B28: copy mode on the platform-admin path only (`instance.binding.write`, the
        // permission of `PUT /platform/instances/:id/binding`, where a copy is saved).
        onStageCopy={
          canWrite
            ? (name) => {
                if (!replacing) return;
                onDraftChange({
                  ...draft,
                  secrets: { ...draft.secrets, [replacing]: { copyFrom: { name } } },
                });
              }
            : undefined
        }
      />

      <ReauthDialog
        open={saver.isReauthOpen}
        isRetrying={saver.isSaving}
        changeSummary={
          <ul className="list-disc pl-5">
            {summariseChanges(draft, binding).map((line) => (
              <li key={line}>
                <SkyText size={200}>{line}</SkyText>
              </li>
            ))}
          </ul>
        }
        error={saver.reauthError}
        code={saver.reauthCode}
        blocked={saver.reauthBlocked}
        remember={saver.reauthRemember}
        onConfirm={() => void saver.retryAfterReauth()}
        onCancel={saver.cancelReauth}
      />
    </>
  );
}

/**
 * Both tabs' drafts, seeded from the FIRST binding the query yields, during render.
 *
 * Seeding in an effect costs a paint of an empty form, and re-seeding whenever
 * the query data changes identity would wipe the user's edits the moment a
 * background refetch resolved — the same class of bug as re-deriving the save
 * body after the re-authentication dialog. Once seeded, only a save reseeds it.
 */
function useSeededDrafts(binding: InstanceBindingView | undefined) {
  const [draft, setDraft] = useState<DestinationDraft | null>(null);
  const [dbtDraft, setDbtDraft] = useState<DbtDraft | null>(null);
  if (binding && draft === null) setDraft(draftFromBinding(binding));
  if (binding && dbtDraft === null) setDbtDraft(dbtDraftFromBinding(binding));
  return { draft, setDraft, dbtDraft, setDbtDraft };
}

/** The server's reason when a save was refused for moving the destination over landed tables. */
function destinationMovedReason(cause: unknown): string | null {
  // #1461: the move has its own confirm code (it was a generic 409 `CONFLICT`).
  if (!isApiErrorCode(cause, "DESTINATION_RESET_CONFIRM")) return null;
  return cause.details.find((detail) => detail.field === "resetBounds")?.issue ?? null;
}

/** Review change 6: a save that leaves the destination Ready is checked, never tested. */
function testModeAfterSave(view: InstanceBindingView): "test" | "check" {
  return view.readiness?.state === "ready" ? "check" : "test";
}

/**
 * #2486: the Draft reasons a test can end. An incomplete destination fails its fields first, and a
 * Databricks one is coming soon, so neither is tested.
 */
const TESTABLE_DRAFT_REASONS: ReadonlySet<string> = new Set([
  "never_tested",
  "changed",
  "test_failed",
]);

/**
 * #2486: the test that follows a Warehouse save, or none. Save and test asked for one (a Ready
 * destination is checked). Any other Save that leaves the destination in Draft tests it too, so
 * imports never wait in Draft until someone comes back to press Test.
 */
function testAfterSaveOf(view: InstanceBindingView, asked: boolean): "test" | "check" | null {
  if (asked) return testModeAfterSave(view);
  const readiness = view.readiness;
  if (readiness?.state !== "draft" || !TESTABLE_DRAFT_REASONS.has(readiness.reason ?? "")) {
    return null;
  }
  return "test";
}

function savedNoticeText(filledElsewhere: boolean, testing: boolean): string {
  if (filledElsewhere) {
    return testing
      ? `${FILLED_ELSEWHERE_NOTICE} skyData is testing it now.`
      : FILLED_ELSEWHERE_NOTICE;
  }
  return testing
    ? "Destination saved. skyData is testing it now."
    : "Destination saved. It applies to the next import.";
}

/**
 * Round 9 (B-02): a save refused because tables have landed, held for the move dialog. #2496:
 * `andTest` keeps Save and test's test through the confirmation.
 */
interface MoveRefused {
  body: WarehouseSaveBody;
  reason: string;
  andTest: boolean;
}

/** How a refused or failed Warehouse save is said: where, and in which words. */
type SaveFailure =
  | { kind: "load-fields" }
  | { kind: "moved"; body: WarehouseSaveBody; reason: string }
  | { kind: "notice" }
  | { kind: "inline" };

/**
 * #1248: the server's refusal of the empty load fields, when the form names them too (a replay can
 * come back before the binding is read, when the form names nothing yet: the fields then show
 * their sentence as soon as it is). Then a save refused for moving over landed tables (Round 9,
 * B-02), then the Save that was pressed: the header's failure is said in the notice, the bottom
 * Save's beside it.
 */
function classifySaveFailure(
  cause: unknown,
  held: {
    draft: DestinationDraft | null;
    binding: InstanceBindingView | undefined;
    lastBody: WarehouseSaveBody | null;
    origin: "header" | "form" | null;
  },
): SaveFailure {
  const { draft, binding, lastBody, origin } = held;
  const namesLoadFields =
    !draft ||
    !binding ||
    serverRefuses(destinationRefusals(draft, binding, buildBody(draft, binding)));
  if (isLoadFieldRefusal(cause) && namesLoadFields) return { kind: "load-fields" };
  const reason = destinationMovedReason(cause);
  if (reason && lastBody) return { kind: "moved", body: lastBody, reason };
  return { kind: origin === "form" ? "inline" : "notice" };
}

/** What a pressed Save does: send, send to be refused and audited, say why not, or nothing. */
type SaveAction = "send" | "send-refused" | "refuse" | "nothing";

function saveActionOf(
  refusals: DestinationRefusals,
  body: WarehouseSaveBody | null,
  saveable: boolean,
): SaveAction {
  // #924: an incomplete Snowflake destination is never saved; its fields say why. #1248: when
  // the server refuses the same fields, the save is sent so the refusal is audited there.
  if (Object.keys(refusals).length > 0) {
    return body && serverRefuses(refusals) ? "send-refused" : "refuse";
  }
  return saveable && body ? "send" : "nothing";
}

/** Round 5: the Database field suggests `<TENANT>_<ENVIRONMENT>`, the label else the slug. */
function suggestedDatabaseOf(
  instanceView: { tenantName: string; environmentLabel?: string | null; slug: string } | undefined,
): string | undefined {
  if (!instanceView) return undefined;
  return suggestedWarehouseDatabase(
    instanceView.tenantName,
    instanceView.environmentLabel ?? instanceView.slug,
  );
}

/** `?tab=dbt` is the dbt repository; anything else is the Warehouse. */
function tabOf(param: string | null): DestinationTab {
  return param === "dbt" ? "dbt" : "warehouse";
}

function isNotFound(isError: boolean, error: unknown): boolean {
  return isError && isNotFoundError(error);
}

/** #923: the state chip and the test, once the binding says where the destination stands. */
function WarehouseTestPanel({
  binding,
  testResult,
  ...rest
}: {
  binding: InstanceBindingView;
  testResult: DestinationTestView | null;
} & Omit<ComponentProps<typeof DestinationTestPanel>, "readiness" | "result">) {
  if (!binding.readiness) return null;
  return (
    <DestinationTestPanel
      readiness={testResult?.readiness ?? binding.readiness}
      result={testResult}
      {...rest}
    />
  );
}

export default function DestinationPage() {
  const { can, canAny, isResolved } = usePermissions();
  const { selectedInstance } = useTenantInstance();
  const notice = useNotice();
  const showSaveErrorNotice = useApiErrorNotice();
  // A failed save is said next to the Save that was pressed (ux-s10 §3; review r1 SF2): the
  // bottom Save's just above the Save row, the page header's "Save changes" in the notice
  // directly under that header (as instance Settings does), because on a long form the bottom
  // row is out of view from the header. `null` is a save with no click on this page load: the
  // replay after a re-authentication redirect (`useSaveBinding`'s handoff), which lands at the
  // top of the form, so its failure goes to the notice too (review r2 SF2).
  const saveError = useInlineError();
  const saveOrigin = useRef<"header" | "form" | null>(null);
  // Round 9 (B-02): the save the server refused because tables have already landed in the current
  // destination. The dialog offers the same save again with `resetBounds`.
  const lastBody = useRef<WarehouseSaveBody | null>(null);
  const [moveRefused, setMoveRefused] = useState<MoveRefused | null>(null);

  // #923: the last Test or Check now pressed here, shown until the next save.
  const {
    tester,
    testResult,
    setTestResult,
    testError,
    testReauth,
    setTestReauth,
    runTest,
    retryTestAfterReauth,
  } = useDestinationTestRun();
  // Save and test (review): set when the pressed Save should be followed by a test, so a save that
  // is replayed after a re-authentication still runs it once it lands.
  const testAfterSave = useRef(false);

  const canRead = canAny("instance.binding.read");
  const canWrite = can("instance.binding.write");
  // Round 5: the Database field suggests `<TENANT>_<ENVIRONMENT>` (the environment label, else
  // the instance's slug). Only a suggestion: an empty field shows it, nothing writes it.
  const instanceView = useInstance(canRead).data;
  const suggestedDatabase = suggestedDatabaseOf(instanceView);
  const canWriteDbt = can("dbt_jobs.write");
  // Owner 2026-09-28: "Platform resources" is for platform admins only, hidden (not disabled)
  // for everyone else and never requested for them. `platform.instances.manage` is the
  // permission of provisioning, and the route checks the same key.
  const canSeePlatformResources = can("platform.instances.manage");
  const [searchParams] = useSearchParams();
  const [, navigate] = useLocation();
  const tab = tabOf(searchParams.get("tab"));

  // See `instanceHeaders` in `use-access.ts`: the header is stated explicitly
  // because this screen can render before `/api/v2/me` resolves, and the getter
  // the fetch layer reads correctly has no instance to give until it does.
  const {
    data: binding,
    isPending,
    isError,
    error,
    refetch,
  } = useBinding(canRead && Boolean(selectedInstance));
  const { draft, setDraft, dbtDraft, setDbtDraft } = useSeededDrafts(binding);
  const [replacing, setReplacing] = useState<BindingSecretField | null>(null);
  // #924, #925: set when a Save was refused here before sending; the fields it named then show
  // their sentence, each until it is fixed, so nothing turns red before the admin presses Save.
  const [showRefusals, setShowRefusals] = useState(false);
  // #1248: set while an incomplete save is sent for the server to refuse and record. Read only
  // to tell an unexpected success apart (someone else filled the empty fields meanwhile); the
  // refusal itself is recognised from the error (`isLoadFieldRefusal`).
  const expectedRefusal = useRef(false);

  const saver = useSaveBinding<DestinationDraft, WarehouseSaveBody>({
    // FR-14: confirming identity can take the user off this page entirely (the
    // MSAL provider redirects), so the draft travels with the held body and is
    // put back here when the identity provider sends them home.
    handoff: {
      returnTo: ROUTES.settingsDestination,
      snapshot: () => draft,
      restore: setDraft,
    },
    onSaved: (view) => {
      setDraft(draftFromBinding(view));
      setTestResult(null);
      const testMode = testAfterSaveOf(view, testAfterSave.current);
      testAfterSave.current = false;
      if (testMode) void runTest(testMode);
      setShowRefusals(false);
      saveError.clear();
      notice.dismiss(SAVE_ERROR_NOTICE_KEY);
      // #1248: sent incomplete, yet saved. The server read the stored fields, which another
      // admin filled meanwhile; the form now shows the saved destination, so say to check it.
      const filledElsewhere = expectedRefusal.current;
      expectedRefusal.current = false;
      notice.success(savedNoticeText(filledElsewhere, testMode !== null), {
        key: SAVED_NOTICE_KEY,
      });
    },
    onError: (cause) => {
      // #2496: Save and test's test survives the move confirmation, so it is read before clearing.
      const andTest = testAfterSave.current;
      testAfterSave.current = false;
      expectedRefusal.current = false;
      const failure = classifySaveFailure(cause, {
        draft,
        binding,
        lastBody: lastBody.current,
        origin: saveOrigin.current,
      });
      switch (failure.kind) {
        case "load-fields":
          // #1248: the server refused the empty load fields the form also names. The fields say
          // what to fill in, so the notice is the same one as before it was sent, and the fields
          // show their sentence again (they are reset by a sign-in redirect, which replays the
          // body).
          setShowRefusals(true);
          saveError.clear();
          notice.error(INCOMPLETE_NOTICE, { key: SAVE_ERROR_NOTICE_KEY });
          return;
        case "moved":
          saveError.clear();
          notice.dismiss(SAVE_ERROR_NOTICE_KEY);
          setMoveRefused({ body: failure.body, reason: failure.reason, andTest });
          return;
        case "notice":
          saveError.clear();
          showSaveErrorNotice(cause, "The destination could not be saved.", {
            key: SAVE_ERROR_NOTICE_KEY,
          });
          return;
        case "inline":
          notice.dismiss(SAVE_ERROR_NOTICE_KEY);
          saveError.show(cause, "The destination could not be saved.");
          return;
      }
    },
  });

  // The transformation source saves to its own route. Its errors are shown inline in the
  // tab (the control that failed is still on screen), so there is no error toast here.
  const dbtSaver = useSaveBinding<DbtDraft, DbtSourceSaveBody>({
    path: DBT_SOURCE_PATH,
    handoff: {
      returnTo: TAB_PATHS.dbt,
      snapshot: () => dbtDraft,
      restore: setDbtDraft,
    },
    onSaved: (view) => {
      setDbtDraft(dbtDraftFromBinding(view));
      notice.success("dbt repository saved. It applies from the next run.", {
        key: DBT_SAVED_NOTICE_KEY,
      });
    },
  });

  const body = useMemo(
    () => (draft && binding ? buildBody(draft, binding) : null),
    [draft, binding],
  );
  // N-9 moved with the dbt fields: the dbt repository tab gates its own
  // save on the shared refusals (`dbtSourceRefusals`). The Warehouse body never
  // carries a dbt field, so nothing here can be refused by those rules.
  const saveable = body !== null;
  const refusals = useMemo(
    () => (draft && binding ? destinationRefusals(draft, binding, body) : {}),
    [draft, binding, body],
  );

  // B28: the server's sentence for a refused copy source, shown at the field it names, for as
  // long as that field still holds the staged change the save was refused for.
  const secretErrors = useMemo(() => {
    const issues = secretFieldIssues(saver.error);
    for (const field of Object.keys(issues) as BindingSecretField[]) {
      if (draft?.secrets[field] === undefined) delete issues[field];
    }
    return issues;
  }, [saver.error, draft]);

  // "Saved" is never shown over unsaved edits (ux-s10 §3; review r1 SF4). Both diffs are null
  // right after a save, because the saver writes the returned view into the query cache first.
  const dbtDirty = useMemo(
    () => (dbtDraft && binding ? buildDbtBody(dbtDraft, binding) !== null : false),
    [dbtDraft, binding],
  );
  useDismissNoticeWhen(SAVED_NOTICE_KEY, saveable);
  useDismissNoticeWhen(DBT_SAVED_NOTICE_KEY, dbtDirty);

  /** The Warehouse save, noting which Save was pressed; the saved view, or null. */
  const save = async (
    origin: "header" | "form",
    andTest = false,
  ): Promise<InstanceBindingView | null> => {
    testAfterSave.current = andTest;
    saveOrigin.current = origin;
    lastBody.current = body;
    const action = saveActionOf(refusals, body, saveable);
    if (action !== "send") testAfterSave.current = false;
    if (action === "send-refused" || action === "refuse") setShowRefusals(true);
    if (action === "send-refused") expectedRefusal.current = true;
    if (action === "refuse") notice.error(INCOMPLETE_NOTICE, { key: SAVE_ERROR_NOTICE_KEY });
    return (action === "send" || action === "send-refused") && body ? saver.save(body) : null;
  };

  // See the note in `pages/admin/users.tsx`: a denial rendered before `/api/v2/me`
  // answers is a denial shown to somebody who is allowed.
  if (!isResolved) return <LoadingRows rows={4} columns={3} />;
  if (!canRead) return <PermissionNotice permission="instance.binding.read" />;

  const notFound = isNotFound(isError, error);

  return (
    <div className="space-y-4">
      <SettingsTabs active="destination" />

      <SkyTabList
        testId="destination-tabs"
        selectedValue={tab}
        tabs={[
          { value: "warehouse", label: "Warehouse" },
          { value: "dbt", label: "dbt repository" },
        ]}
        onTabSelect={(value) => {
          if (value === tab) return;
          navigate(absolutePath(TAB_PATHS[value === "dbt" ? "dbt" : "warehouse"]));
        }}
      />

      {tab === "dbt" ? (
        <BindingRead
          isPending={isPending}
          notFound={notFound}
          isError={isError}
          error={error}
          onRetry={() => void refetch()}
          emptyMessage="A platform admin binds the destination first. The dbt repository is part of it."
        >
          <DbtTabForm
            binding={binding}
            draft={dbtDraft}
            canEdit={canWriteDbt}
            // B28: copy mode is the platform route's, which needs `instance.binding.write`.
            allowCopy={canWrite}
            saver={dbtSaver}
            onDraftChange={setDbtDraft}
          />
        </BindingRead>
      ) : (
        <>
          {/* ADR 0011: where the data lands and how to open it, for every reader of this tab. */}
          <SkyLink
            as={Link}
            href={absolutePath(ROUTES.yourData)}
            testId="destination-your-data-link"
          >
            Where your data lands
          </SkyLink>
          <WarehouseIntro canWrite={canWrite} />

          <BindingRead
            isPending={isPending}
            notFound={notFound}
            isError={isError}
            error={error}
            onRetry={() => void refetch()}
            emptyMessage="Pick Snowflake or Databricks and fill in the connection details. Until then, runs stage files in the lake but load nothing."
          >
            {binding && draft ? (
              <WarehouseEditor
                binding={binding}
                draft={draft}
                onDraftChange={setDraft}
                canWrite={canWrite}
                secretErrors={secretErrors}
                refusals={showRefusals ? refusals : {}}
                suggestedDatabase={suggestedDatabase}
                saver={saver}
                saveable={saveable}
                saveFailure={saveError.error}
                onDismissSaveFailure={saveError.clear}
                onSave={(origin) => void save(origin)}
                discardable={body !== null}
                replacing={replacing}
                onReplacingChange={setReplacing}
                testPanel={
                  <WarehouseTestPanel
                    binding={binding}
                    testResult={testResult}
                    canWrite={canWrite}
                    dirty={saveable}
                    running={tester.isPending || saver.isSaving}
                    failure={testError.error}
                    onDismissFailure={testError.clear}
                    onTest={(mode) => void runTest(mode)}
                    onSaveAndTest={() => void save("form", true)}
                  />
                }
              />
            ) : null}
          </BindingRead>

          <MoveRefusedDialog
            moveRefused={moveRefused}
            isSaving={saver.isSaving}
            onKeep={() => setMoveRefused(null)}
            onSaveAgain={() => {
              if (!moveRefused) return;
              const held = moveRefused.body;
              testAfterSave.current = moveRefused.andTest;
              setMoveRefused(null);
              saveOrigin.current = "form";
              lastBody.current = { ...held, resetBounds: true };
              void saver.save({ ...held, resetBounds: true });
            }}
          />

          <TestReauthDialog
            testReauth={testReauth}
            isRetrying={tester.isPending}
            remember={saver.reauthRemember}
            onConfirm={() => void retryTestAfterReauth()}
            onCancel={() => setTestReauth(null)}
          />

          {/* Its own route and its own states: shown whether or not a binding exists yet. */}
          {canSeePlatformResources ? <PlatformResources /> : null}
        </>
      )}
    </div>
  );
}
